// Text matching for the Query RegX notes filters (PatientQueryPage).
//
// "Notes contains" searches three places:
//   - the legacy `notes` field (consents/duplicates doc, or the Sheet NOTES column)
//   - the coordinator notes timeline (patient_notes/{id}/notes)
//   - replies under those notes (…/notes/{noteId}/replies)
// "Not eligible reason contains" searches the reason staff type when they mark
// a patient Not Eligible for a study (studyEligibility[studyId].reason).
//
// Pure and Firebase-free so it's unit tested; the page loads the data.

const lower = (s) => String(s || "").toLowerCase();

/**
 * The Not Eligible reasons on a patient, one per study. Eligible entries and
 * Not Eligible entries with no reason typed are left out.
 * @param {object} studyEligibility - { [studyDocId]: { status, reason, ... } }
 * @returns {Array<{studyId: string, reason: string}>}
 */
export const notEligibleReasons = (studyEligibility) => {
  if (!studyEligibility || typeof studyEligibility !== "object") return [];
  return Object.entries(studyEligibility)
    .filter(([, v]) => v && v.status === "notEligible" && String(v.reason || "").trim())
    .map(([studyId, v]) => ({ studyId, reason: String(v.reason).trim() }));
};

// All reasons as one string, for the generic rule operators.
export const notEligibleReasonText = (studyEligibility) =>
  notEligibleReasons(studyEligibility)
    .map((r) => r.reason)
    .join("\n");

/**
 * A short excerpt around the first case-insensitive match of `term`.
 * Returns "" when there's no match.
 */
export const snippetAround = (text, term, radius = 40) => {
  const s = String(text || "").replace(/\s+/g, " ").trim();
  const t = lower(term).trim();
  if (!s || !t) return "";
  const i = lower(s).indexOf(t);
  if (i < 0) return "";
  const start = Math.max(0, i - radius);
  const end = Math.min(s.length, i + t.length + radius);
  return `${start > 0 ? "…" : ""}${s.slice(start, end)}${end < s.length ? "…" : ""}`;
};

/**
 * First place the term appears across legacy notes, coordinator notes and
 * replies, in that order. Null when nowhere.
 * @param {string} legacyNotes
 * @param {Array<{kind: "note"|"reply", body: string, createdAt?: any}>} coordinatorEntries
 * @param {string} term
 * @returns {{where: "legacy"|"note"|"reply", createdAt: any, snippet: string} | null}
 */
export const notesContain = (legacyNotes, coordinatorEntries, term) => {
  const t = lower(term).trim();
  if (!t) return null;
  if (lower(legacyNotes).includes(t)) {
    return { where: "legacy", createdAt: null, snippet: snippetAround(legacyNotes, term) };
  }
  for (const e of coordinatorEntries || []) {
    if (e && lower(e.body).includes(t)) {
      return { where: e.kind === "reply" ? "reply" : "note", createdAt: e.createdAt || null, snippet: snippetAround(e.body, term) };
    }
  }
  return null;
};

/**
 * First Not Eligible reason containing the term. Null when none.
 * @returns {{studyId: string, snippet: string} | null}
 */
export const notEligibleContains = (studyEligibility, term) => {
  const t = lower(term).trim();
  if (!t) return null;
  const hit = notEligibleReasons(studyEligibility).find((r) => lower(r.reason).includes(t));
  return hit ? { studyId: hit.studyId, snippet: snippetAround(hit.reason, term) } : null;
};
